import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { AssignmentSubTask, ProjectAssignmentItem } from '../../types/assignmentPointsNote';
import {
  defaultChallengeContent,
  emptyChallengeMission,
  CHALLENGE_MISSION_MAX,
  challengeThemeStyle,
  normalizeChallengeIntro,
  DEFAULT_CHALLENGE_INTRO,
  type ChallengeItem,
  type ChallengeProject,
  type ChallengeProjectIntro,
} from '../../types/assignmentChallenge';
import {
  fetchChallengeBundle,
  formatChallengeError,
  saveChallengeItems,
} from '../../services/assignmentChallengeService';
import { deleteAssignmentCoverImage } from '../../services/assignmentCoverImageStorageService';
import {
  ChallengeListCard,
  ChallengeLogoBar,
  ChallengeMissionScreen,
  ChallengeProjectHead,
  ChallengeSuccessCard,
  ChallengeThumb,
  type ChallengeEditApi,
  type ChallengeIntroEditApi,
} from './ChallengeStage';
import ChallengeThumbnailModal from './ChallengeThumbnailModal';
import { newId } from '../assignmentPoints/assignmentCreateModalShared';

type Screen = 'form' | 'success';

type Props = {
  open: boolean;
  item: ProjectAssignmentItem | null;
  defaultPoints: number;
  /** 과제 썸네일(정사각) URL — 설명 화면 썸네일로 쓴다 */
  coverImageUrl?: string;
  /** 하위 챌린지별 제출 수(삭제 경고용) */
  submissionCountByItemId?: Record<string, number>;
  onClose: () => void;
  onSaved: (subTasks: AssignmentSubTask[], project: ChallengeProject) => Promise<void> | void;
};

type Draft = {
  items: ChallengeItem[];
  points: Record<string, number>;
  intro: ChallengeProjectIntro;
};

/** 목록 맨 위 「챌린지 프로젝트 설명」 카드 선택 값 */
const INTRO_ID = '__intro__';

function snapshot(d: Draft): string {
  return JSON.stringify({
    items: d.items.map((i) => ({
      id: i.id,
      v: i.isVisible,
      t: i.title,
      c: i.content,
      th: i.thumbnailPath,
    })),
    points: d.points,
    intro: d.intro,
  });
}

function SortableChallengeRow({
  item,
  index,
  active,
  points,
  pointStep,
  onSelect,
  onToggleVisible,
  onPoints,
  onDelete,
  onThumbnail,
  canDelete,
}: {
  item: ChallengeItem;
  index: number;
  active: boolean;
  points: number;
  pointStep: number;
  onSelect: () => void;
  onToggleVisible: () => void;
  onPoints: (v: number) => void;
  onDelete: () => void;
  onThumbnail: () => void;
  canDelete: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const step = pointStep > 0 ? pointStep : 1;
  return (
    <li
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 5 : undefined,
      }}
      className={`cp-chl-editor-row${active ? ' is-active' : ''}${isDragging ? ' is-dragging' : ''}${
        item.isVisible ? '' : ' is-hidden'
      }`}
    >
      <button
        type="button"
        className="cp-chl-editor-row-main"
        onClick={onSelect}
        aria-current={active ? 'true' : undefined}
        {...attributes}
        {...listeners}
        aria-label={`${index + 1}번 인증과제 ${item.title || '제목 없음'} 편집 (끌어서 순서 변경)`}
      >
        <span className="cp-chl-editor-row-thumb">
          <ChallengeThumb url={item.thumbnailUrl} title={item.title} />
          <span className="cp-chl-editor-row-no">{index + 1}</span>
          {item.isVisible ? null : <span className="cp-chl-editor-row-hidden">숨김</span>}
        </span>
        <span className="cp-chl-editor-row-text">
          {item.content.kicker ? <span className="cp-chl-editor-row-kicker">{item.content.kicker}</span> : null}
          <span className="cp-chl-editor-row-title">{item.title || '제목 없는 인증과제'}</span>
        </span>
      </button>
      <button
        type="button"
        className="cp-chl-editor-row-thumb-btn"
        onClick={onThumbnail}
        aria-label={`${index + 1}번 인증과제 썸네일 ${item.thumbnailUrl ? '변경' : '등록'}`}
        title={item.thumbnailUrl ? '썸네일 변경' : '썸네일 등록'}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
          <rect x="3" y="4.5" width="18" height="15" rx="3" stroke="currentColor" strokeWidth="2" />
          <circle cx="9" cy="10" r="1.8" fill="currentColor" />
          <path d="m4 18 5-5 3.5 3.5L15 14l5 4.5" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
        </svg>
      </button>
      <div className="cp-chl-editor-row-tools">
        <button
          type="button"
          className={`cp-chl-visibility${item.isVisible ? ' is-on' : ''}`}
          aria-pressed={item.isVisible}
          onClick={onToggleVisible}
          title={item.isVisible ? '학생에게 공개 중' : '학생에게 숨김'}
        >
          <span className="cp-chl-visibility-dot" aria-hidden />
          {item.isVisible ? '공개' : '숨김'}
        </button>
        <div className="cp-chl-editor-points">
          <button
            type="button"
            className="cp-chl-editor-points-btn"
            onClick={() => onPoints(Math.max(0, points - step))}
            disabled={points <= 0}
            aria-label={`${index + 1}번 인증과제 포인트 ${step}점 내리기`}
          >
            −
          </button>
          <input
            type="number"
            min={0}
            value={points}
            onChange={(e) => onPoints(Math.max(0, Number(e.target.value) || 0))}
            aria-label={`${index + 1}번 인증과제 포인트`}
          />
          <button
            type="button"
            className="cp-chl-editor-points-btn"
            onClick={() => onPoints(points + step)}
            aria-label={`${index + 1}번 인증과제 포인트 ${step}점 올리기`}
          >
            +
          </button>
          <span className="cp-chl-editor-points-unit">점</span>
        </div>
        <button
          type="button"
          className="cp-chl-editor-row-delete"
          onClick={onDelete}
          disabled={!canDelete}
          aria-label={`${index + 1}번 인증과제 삭제`}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>
    </li>
  );
}

const ChallengeEditorModal: React.FC<Props> = ({
  open,
  item,
  defaultPoints,
  coverImageUrl = '',
  submissionCountByItemId = {},
  onClose,
  onSaved,
}) => {
  const projectId = item?.challenge?.projectId ?? '';
  const [project, setProject] = useState<ChallengeProject | null>(null);
  const [draft, setDraft] = useState<Draft>({
    items: [],
    points: {},
    intro: DEFAULT_CHALLENGE_INTRO,
  });
  const [baselineItems, setBaselineItems] = useState<ChallengeItem[]>([]);
  const baselineRef = useRef('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [screen, setScreen] = useState<Screen>('form');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [thumbOpen, setThumbOpen] = useState(false);
  const baselineIntroThumbRef = useRef<string | null>(null);
  const [leaveConfirm, setLeaveConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ChallengeItem | null>(null);
  const sessionThumbsRef = useRef<Set<string>>(new Set());

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const applyBundle = useCallback(
    (bundle: { project: ChallengeProject; items: ChallengeItem[] }) => {
      if (!item) return;
      const byId = new Map(bundle.items.map((i) => [i.id, i]));
      const items: ChallengeItem[] = item.subTasks.map((s, index) => {
        const found = byId.get(s.id);
        return found
          ? { ...found, title: found.title || s.title, sortOrder: index }
          : {
              id: s.id,
              projectId: bundle.project.id,
              sortOrder: index,
              isVisible: true,
              title: s.title,
              content: defaultChallengeContent(),
              thumbnailUrl: null,
              thumbnailPath: null,
              revision: 1,
              updatedAt: '',
            };
      });
      const points: Record<string, number> = {};
      item.subTasks.forEach((s) => {
        points[s.id] = s.points;
      });
      const intro = normalizeChallengeIntro(bundle.project.intro, bundle.items[0]?.content ?? items[0]?.content);
      const next = { items, points, intro };
      setProject(bundle.project);
      setBaselineItems(bundle.items);
      setDraft(next);
      baselineRef.current = snapshot(next);
      baselineIntroThumbRef.current = intro.thumbnailPath;
      setSelectedId((cur) =>
        cur && (cur === INTRO_ID || items.some((i) => i.id === cur)) ? cur : (items[0]?.id ?? INTRO_ID)
      );
    },
    [item]
  );

  useEffect(() => {
    if (!open || !projectId) return;
    let cancelled = false;
    setError('');
    setScreen('form');
    setLeaveConfirm(false);
    setDeleteTarget(null);
    sessionThumbsRef.current = new Set();
    setLoading(true);
    void fetchChallengeBundle(projectId, {
      onCached: (b) => {
        if (!cancelled) applyBundle(b);
      },
    })
      .then((b) => {
        if (cancelled) return;
        if (!b) {
          setError('챌린지 정보를 찾을 수 없습니다.');
          return;
        }
        applyBundle(b);
      })
      .catch((e) => {
        if (!cancelled) setError(formatChallengeError(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, projectId, applyBundle]);

  const dirty = open && baselineRef.current !== '' && snapshot(draft) !== baselineRef.current;

  const selected = useMemo(() => draft.items.find((i) => i.id === selectedId) ?? null, [draft.items, selectedId]);

  const introSelected = selectedId === INTRO_ID;
  const visibleItems = useMemo(() => draft.items.filter((i) => i.isVisible), [draft.items]);

  const patchSelected = useCallback(
    (fn: (cur: ChallengeItem) => ChallengeItem) => {
      if (!selectedId) return;
      setDraft((d) => ({
        ...d,
        items: d.items.map((i) => (i.id === selectedId ? fn(i) : i)),
      }));
    },
    [selectedId]
  );

  const editApi: ChallengeEditApi = useMemo(
    () => ({
      setTitle: (v) => patchSelected((i) => ({ ...i, title: v.trim() ? v : i.title })),
      setCopy: (key, v) => patchSelected((i) => ({ ...i, content: { ...i.content, [key]: v } })),
      updateMission: (id, patch) =>
        patchSelected((i) => ({
          ...i,
          content: {
            ...i.content,
            missions: i.content.missions.map((m) => (m.id === id ? { ...m, ...patch } : m)),
          },
        })),
      addMission: () =>
        patchSelected((i) =>
          i.content.missions.length >= CHALLENGE_MISSION_MAX
            ? i
            : {
                ...i,
                content: {
                  ...i.content,
                  missions: [
                    ...i.content.missions,
                    {
                      ...emptyChallengeMission(),
                      title: `인증 항목 ${i.content.missions.length + 1}`,
                    },
                  ],
                },
              }
        ),
      removeMission: (id) =>
        patchSelected((i) =>
          i.content.missions.length <= 1
            ? i
            : {
                ...i,
                content: {
                  ...i.content,
                  missions: i.content.missions.filter((m) => m.id !== id),
                },
              }
        ),
      reorderMissions: (activeId, overId) =>
        patchSelected((i) => {
          const from = i.content.missions.findIndex((m) => m.id === activeId);
          const to = i.content.missions.findIndex((m) => m.id === overId);
          if (from < 0 || to < 0) return i;
          return {
            ...i,
            content: {
              ...i.content,
              missions: arrayMove(i.content.missions, from, to),
            },
          };
        }),
      openThumbnail: () => setThumbOpen(true),
    }),
    [patchSelected]
  );

  const setIntro = useCallback((key: keyof ChallengeProjectIntro, v: string) => {
    setDraft((d) => ({ ...d, intro: { ...d.intro, [key]: v } }));
  }, []);

  const introEditApi: ChallengeEditApi = useMemo(
    () => ({
      setTitle: () => undefined,
      setCopy: (key, v) => {
        if (key === 'topLogoText' || key === 'bottomLogoText') setIntro(key, v);
      },
      updateMission: () => undefined,
      addMission: () => undefined,
      removeMission: () => undefined,
      reorderMissions: () => undefined,
      openThumbnail: () => undefined,
    }),
    [setIntro]
  );

  const introHeadEditApi: ChallengeIntroEditApi = useMemo(
    () => ({ setIntro: (key, v) => setIntro(key, v) }),
    [setIntro]
  );
  const introWithCover = useMemo(
    () => ({ ...draft.intro, thumbnailUrl: coverImageUrl || null, thumbnailPath: null }),
    [draft.intro, coverImageUrl]
  );

  const addChallenge = () => {
    const id = newId('sub');
    const next: ChallengeItem = {
      id,
      projectId,
      sortOrder: draft.items.length,
      isVisible: true,
      title: `인증과제 ${draft.items.length + 1}`,
      content: defaultChallengeContent(),
      thumbnailUrl: null,
      thumbnailPath: null,
      revision: 1,
      updatedAt: '',
    };
    setDraft((d) => ({
      ...d,
      items: [...d.items, next],
      points: { ...d.points, [id]: defaultPoints },
    }));
    setSelectedId(id);
    setScreen('form');
  };

  const onRowsDragEnd = (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return;
    setDraft((d) => {
      const from = d.items.findIndex((i) => i.id === e.active.id);
      const to = d.items.findIndex((i) => i.id === e.over!.id);
      if (from < 0 || to < 0) return d;
      return { ...d, items: arrayMove(d.items, from, to) };
    });
  };

  const discardSessionThumbs = useCallback((keep: Set<string>) => {
    const paths = Array.from(sessionThumbsRef.current).filter((p) => !keep.has(p));
    sessionThumbsRef.current = new Set();
    paths.forEach((p) => {
      void deleteAssignmentCoverImage(p).catch(() => undefined);
    });
  }, []);

  const closeWithoutSave = () => {
    const keep = new Set(
      [...baselineItems.map((i) => i.thumbnailPath), baselineIntroThumbRef.current].filter(Boolean) as string[]
    );
    discardSessionThumbs(keep);
    setLeaveConfirm(false);
    onClose();
  };

  const requestClose = () => {
    if (saving) return;
    if (dirty) {
      setLeaveConfirm(true);
      return;
    }
    closeWithoutSave();
  };

  const save = async (closeAfter: boolean) => {
    if (!project || saving) return;
    const invalid = draft.items.find((i) => !i.title.trim());
    if (draft.items.length === 0) {
      setError('인증과제를 1개 이상 만들어 주세요.');
      return;
    }
    if (invalid) {
      setError('제목이 비어 있는 인증과제가 있습니다.');
      setSelectedId(invalid.id);
      return;
    }
    setSaving(true);
    setError('');
    try {
      const bundle = await saveChallengeItems({
        project,
        title: item?.title ?? project.title,
        intro: introWithCover,
        items: draft.items.map((i, index) => ({
          ...i,
          title: i.title.trim(),
          sortOrder: index,
        })),
        previous: baselineItems,
      });
      const subTasks: AssignmentSubTask[] = draft.items.map((i, index) => ({
        id: i.id,
        title: i.title.trim(),
        points: draft.points[i.id] ?? defaultPoints,
        order: index + 1,
      }));
      await onSaved(subTasks, bundle.project);
      const keep = new Set(draft.items.map((i) => i.thumbnailPath).filter(Boolean) as string[]);
      const prevIntroThumb = baselineIntroThumbRef.current;
      if (prevIntroThumb) void deleteAssignmentCoverImage(prevIntroThumb).catch(() => undefined);
      baselineIntroThumbRef.current = null;
      discardSessionThumbs(keep);
      setBaselineItems(bundle.items);
      baselineRef.current = snapshot(draft);
      setLeaveConfirm(false);
      if (bundle.introSkipped) {
        setError(
          '인증과제는 저장됐지만 챌린지 프로젝트 설명은 저장되지 않았어요. DB 업데이트(20261016120000_challenge_project_intro.sql)를 적용한 뒤 다시 저장해 주세요.'
        );
        return;
      }
      if (closeAfter) onClose();
    } catch (e) {
      setError(formatChallengeError(e));
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open || thumbOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      const target = e.target as HTMLElement | null;
      if (target?.closest('.cp-chl-inline-editor')) return;
      if (leaveConfirm) {
        setLeaveConfirm(false);
        return;
      }
      if (deleteTarget) {
        setDeleteTarget(null);
        return;
      }
      requestClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  if (!open || !item) return null;

  const deleteCount = deleteTarget ? (submissionCountByItemId[deleteTarget.id] ?? 0) : 0;

  return createPortal(
    <div className="cp-chl-editor-root" role="presentation" style={challengeThemeStyle(item.color)}>
      <div className="cp-chl-editor-dim" aria-hidden onClick={requestClose} />
      <div className="cp-chl-editor" role="dialog" aria-modal="true" aria-label="인증과제 편집">
        <header className="cp-chl-editor-head">
          <div className="cp-chl-editor-head-title">
            <span className="cp-chl-editor-badge">인증과제 편집</span>
            <h2>{item.title || '챌린지 프로젝트'}</h2>
          </div>
          <div className="cp-chl-editor-head-actions">
            {dirty ? <span className="cp-chl-editor-dirty">저장 안 된 변경 있음</span> : null}
            <button type="button" className="cp-chl-editor-head-btn" onClick={requestClose} disabled={saving}>
              닫기
            </button>
            <button
              type="button"
              className="cp-chl-editor-head-btn is-save"
              onClick={() => void save(false)}
              disabled={saving || loading || !dirty}
            >
              {saving ? '저장 중…' : '저장'}
            </button>
          </div>
        </header>
        {error ? (
          <p className="cp-chl-editor-error" role="alert">
            {error}
          </p>
        ) : null}
        <div className="cp-chl-editor-body">
          <aside className="cp-chl-editor-side" aria-label="인증과제 목록">
            <button
              type="button"
              className={`cp-chl-editor-intro-card${introSelected ? ' is-active' : ''}`}
              onClick={() => setSelectedId(INTRO_ID)}
              aria-current={introSelected ? 'true' : undefined}
            >
              <span className="cp-chl-editor-intro-preview" aria-hidden>
                {draft.intro.eyebrow ? (
                  <span className="cp-chl-editor-intro-preview-eyebrow">{draft.intro.eyebrow}</span>
                ) : null}
                {draft.intro.subtitle ? (
                  <span className="cp-chl-editor-intro-preview-subtitle">{draft.intro.subtitle}</span>
                ) : null}
                <span className="cp-chl-editor-intro-preview-title">
                  {draft.intro.headline || item.title || '챌린지 프로젝트'}
                </span>
                <span className={`cp-chl-editor-intro-preview-tile${coverImageUrl ? ' has-image' : ''}`}>
                  {coverImageUrl ? (
                    <img src={coverImageUrl} alt="" />
                  ) : (
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                      <path d="M5 21V4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      <path
                        d="M5 4.5c2.6-1.4 5-1.4 7.2 0s4.6 1.4 6.8 0v9c-2.2 1.4-4.6 1.4-6.8 0s-4.6-1.4-7.2 0"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </span>
                {draft.intro.description ? (
                  <span className="cp-chl-editor-intro-preview-desc">{draft.intro.description}</span>
                ) : null}
              </span>
              <span className="cp-chl-editor-intro-body">
                <span className="cp-chl-editor-intro-label">
                  챌린지 프로젝트 설명
                  <span className="cp-chl-editor-intro-tag">기본</span>
                </span>
                <span className="cp-chl-editor-intro-title">참여 링크 첫 화면</span>
              </span>
            </button>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onRowsDragEnd}>
              <SortableContext items={draft.items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
                <ol className="cp-chl-editor-list">
                  {draft.items.map((ci, index) => (
                    <SortableChallengeRow
                      key={ci.id}
                      item={ci}
                      index={index}
                      active={ci.id === selectedId}
                      points={draft.points[ci.id] ?? defaultPoints}
                      pointStep={defaultPoints}
                      onSelect={() => setSelectedId(ci.id)}
                      onToggleVisible={() =>
                        setDraft((d) => ({
                          ...d,
                          items: d.items.map((x) => (x.id === ci.id ? { ...x, isVisible: !x.isVisible } : x)),
                        }))
                      }
                      onPoints={(v) =>
                        setDraft((d) => ({
                          ...d,
                          points: { ...d.points, [ci.id]: v },
                        }))
                      }
                      onDelete={() => setDeleteTarget(ci)}
                      onThumbnail={() => {
                        setSelectedId(ci.id);
                        setThumbOpen(true);
                      }}
                      canDelete={draft.items.length > 1}
                    />
                  ))}
                </ol>
              </SortableContext>
            </DndContext>
            {loading && draft.items.length === 0 ? <p className="cp-chl-editor-hint">불러오는 중…</p> : null}
            <button type="button" className="cp-chl-editor-add" onClick={addChallenge} disabled={loading}>
              + 인증과제 추가
            </button>
          </aside>
          <main className="cp-chl-editor-stage">
            {introSelected ? (
              <>
                <div className="cp-chl-editor-tabs" role="tablist" aria-label="미리보기 화면">
                  <button type="button" role="tab" aria-selected className="cp-chl-editor-tab is-active">
                    설명 화면
                  </button>
                </div>
                <div className="cp-chl-device">
                  <div className="cp-chl-app is-editing">
                    <section className="cp-chl-screen cp-chl-list">
                      <ChallengeLogoBar text={draft.intro.topLogoText} position="top" edit={introEditApi} />
                      <ChallengeProjectHead
                        memberName="학생"
                        title={item.title}
                        intro={introWithCover}
                        done={0}
                        total={visibleItems.length}
                        onLeave={() => undefined}
                        edit={introHeadEditApi}
                      />
                      <div className="cp-chl-card-list">
                        {visibleItems.map((ci) => (
                          <ChallengeListCard
                            key={ci.id}
                            item={ci}
                            status="todo"
                            onClick={() => {
                              setSelectedId(ci.id);
                              setScreen('form');
                            }}
                          />
                        ))}
                      </div>
                      <ChallengeLogoBar text={draft.intro.bottomLogoText} position="bottom" edit={introEditApi} />
                    </section>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="cp-chl-editor-tabs" role="tablist" aria-label="미리보기 화면">
                  {(
                    [
                      ['form', '인증 화면'],
                      ['success', '완료 팝업'],
                    ] as const
                  ).map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      role="tab"
                      aria-selected={screen === key}
                      className={`cp-chl-editor-tab${screen === key ? ' is-active' : ''}`}
                      onClick={() => setScreen(key)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {selected ? (
                  <div className="cp-chl-device">
                    <div className="cp-chl-app is-editing">
                      <ChallengeLogoBar text={selected.content.topLogoText} position="top" edit={editApi} />
                      {screen === 'form' ? (
                        <ChallengeMissionScreen item={selected} edit={editApi} />
                      ) : (
                        <div className="cp-chl-success-preview">
                          <ChallengeSuccessCard content={selected.content} edit={editApi} />
                        </div>
                      )}
                      <ChallengeLogoBar text={selected.content.bottomLogoText} position="bottom" edit={editApi} />
                    </div>
                  </div>
                ) : (
                  <p className="cp-chl-editor-hint">왼쪽에서 인증과제를 선택하거나 추가해 주세요.</p>
                )}
              </>
            )}
          </main>
        </div>
      </div>

      <ChallengeThumbnailModal
        open={thumbOpen}
        themeColor={item.color}
        currentUrl={selected?.thumbnailUrl ?? null}
        onClose={() => setThumbOpen(false)}
        onUploaded={(thumb) => {
          sessionThumbsRef.current.add(thumb.path);
          patchSelected((i) => ({
            ...i,
            thumbnailUrl: thumb.url,
            thumbnailPath: thumb.path,
          }));
        }}
        onClear={() =>
          patchSelected((i) => ({
            ...i,
            thumbnailUrl: null,
            thumbnailPath: null,
          }))
        }
      />

      {deleteTarget ? (
        <div
          className="cp-modal-backdrop cp-chl-confirm-backdrop"
          role="presentation"
          onClick={() => setDeleteTarget(null)}
        >
          <div
            className="cp-modal cp-leave-modal-box"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="cp-chl-delete-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="cp-chl-delete-title" className="cp-leave-modal-title">
              「{deleteTarget.title || '인증과제'}」 삭제
            </h3>
            <p className="cp-leave-modal-desc">
              {deleteCount > 0
                ? `제출 기록 ${deleteCount}건과 인증 사진도 저장할 때 함께 삭제됩니다.`
                : '저장하면 이 인증과제가 삭제됩니다.'}
            </p>
            <div className="cp-modal-footer cp-leave-modal-actions">
              <button type="button" className="cp-btn-secondary" onClick={() => setDeleteTarget(null)}>
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary cp-chl-danger-btn"
                onClick={() => {
                  const id = deleteTarget.id;
                  setDraft((d) => {
                    const items = d.items.filter((i) => i.id !== id);
                    const points = { ...d.points };
                    delete points[id];
                    return { ...d, items, points };
                  });
                  setSelectedId((cur) => (cur === id ? (draft.items.find((i) => i.id !== id)?.id ?? INTRO_ID) : cur));
                  setDeleteTarget(null);
                }}
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {leaveConfirm ? (
        <div
          className="cp-modal-backdrop cp-chl-confirm-backdrop"
          role="presentation"
          onClick={() => setLeaveConfirm(false)}
        >
          <div
            className="cp-modal cp-leave-modal-box"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="cp-chl-leave-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="cp-chl-leave-title" className="cp-leave-modal-title">
              변경사항이 저장되지 않았습니다
            </h3>
            <p className="cp-leave-modal-desc">저장하고 닫을까요?</p>
            <div className="cp-modal-footer cp-leave-modal-actions">
              <button type="button" className="cp-btn-secondary" onClick={closeWithoutSave} disabled={saving}>
                저장하지 않고 닫기
              </button>
              <button type="button" className="cp-btn-primary" onClick={() => void save(true)} disabled={saving}>
                {saving ? '저장 중…' : '저장하고 닫기'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>,
    document.body
  );
};

export default ChallengeEditorModal;
